import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Column } from 'react-table';
import DefaultLayout from '../../layout/DefaultLayout';
import BreadcrumbTwo from '../../components/Breadcrumbs/BreadcrumbTwo';
import DataTable from '../../components/DataTable/DataTable';
import CashClosureHistoryFilters from '../../components/Cash/ClosuresHistory/CashClosureHistoryFilters';
import CashClosureDetailModal from '../../components/Cash/ClosuresHistory/CashClosureDetailModal';
import CashClosureHistoryService, {
  CashClosureHistoryFilters as FiltersType,
} from '../../services/cash/CashClosureHistoryService';
import CashZPdfService from '../../services/pdf/CashZPdfService';
import { usePrintService } from '../../hooks/printing/usePrintService';
import { useBranch } from '../../hooks/branches/useBranch';
import { formatCurrency } from '../../utils/formatCurrency';
import useAlerts from '../../hooks/useAlerts';

interface ClosureRow {
  closureId: number;
  closureNumber: string;
  closureDateTime: string;
  branchName: string;
  registerName: string;
  closedByName: string;
  totalSales: number;
  difference: number;
}

const CashClosuresHistoryPage: React.FC = () => {
  const { page } = useParams();
  const currentPage = page ? parseInt(page, 10) : 0;
  const pageSize = 10;

  const { fetchBranchDropdown, branchDropdown } = useBranch();
  const { requestLegacyPrint } = usePrintService();
  const { show: showAlert } = useAlerts();

  const [rows, setRows] = useState<ClosureRow[]>([]);
  const [paginator, setPaginator] = useState<any>(null);
  const [filters, setFilters] = useState<FiltersType>({});
  const [selectedClosureId, setSelectedClosureId] = useState<number | null>(
    null
  );

  const fetchHistory = async (
    appliedFilters: FiltersType,
    pageIndex: number
  ) => {
    const response = await CashClosureHistoryService.search(
      appliedFilters,
      pageIndex,
      pageSize
    );
    const data = response.data;
    setRows(data?.content ?? []);
    setPaginator(data);
  };

  useEffect(() => {
    fetchBranchDropdown();
  }, []);

  useEffect(() => {
    fetchHistory(filters, currentPage);
  }, [currentPage]);

  const handleSearch = (newFilters: FiltersType) => {
    setFilters(newFilters);
    fetchHistory(newFilters, 0);
  };

  const handleReset = () => {
    setFilters({});
    fetchHistory({}, 0);
  };

  const handleDownloadPdf = async (row: ClosureRow) => {
    try {
      const { blob, fileName } = await CashZPdfService.download(
        row.closureId
      );
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName || `corte_z_${row.closureId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      showAlert('error', 'Error', 'No se pudo descargar el PDF del cierre.');
    }
  };

  const handleReprint = async (row: ClosureRow) => {
    try {
      await requestLegacyPrint('CASH_Z', row.closureId);
    } catch {
      // usePrintService ya muestra la alerta de error correspondiente
    }
  };

  const columns: Column<ClosureRow>[] = [
    { Header: 'No. Cierre', accessor: 'closureNumber' },
    {
      Header: 'Fecha',
      accessor: 'closureDateTime',
      Cell: ({ value }) => new Date(value).toLocaleString(),
    },
    { Header: 'Sucursal', accessor: 'branchName' },
    { Header: 'Caja', accessor: 'registerName' },
    { Header: 'Cerrado por', accessor: 'closedByName' },
    {
      Header: 'Total Ventas',
      accessor: 'totalSales',
      Cell: ({ value }) => `$${formatCurrency(value)}`,
    },
    {
      Header: 'Diferencia',
      accessor: 'difference',
      Cell: ({ value }) => (
        <span
          className={Number(value) < 0 ? 'text-danger' : 'text-black dark:text-white'}
        >
          ${formatCurrency(value)}
        </span>
      ),
    },
  ];

  const actions = [
    { key: 'view', onClick: (row: ClosureRow) => setSelectedClosureId(row.closureId) },
    { key: 'download', onClick: handleDownloadPdf },
    { key: 'print', onClick: handleReprint },
  ];

  return (
    <DefaultLayout>
      <BreadcrumbTwo />

      <div className="mb-5 rounded-sm border border-stroke bg-white p-5 shadow-default dark:border-strokedark dark:bg-boxdark">
        <CashClosureHistoryFilters
          branches={branchDropdown}
          onSearch={handleSearch}
          onReset={handleReset}
        />
      </div>

      <DataTable
        columns={columns}
        data={rows}
        actions={actions}
        pageSizeOptions={[10, 20, 50]}
        url="/cash/closures-history"
        paginator={paginator}
        onPageSizeChange={() => undefined}
        noDataMessage="No hay cierres de caja registrados con estos filtros."
      />

      <CashClosureDetailModal
        closureId={selectedClosureId}
        onClose={() => setSelectedClosureId(null)}
      />
    </DefaultLayout>
  );
};

export default CashClosuresHistoryPage;
